import { evaluateAuth, type AuthDecision, type AuthRequest } from './auth'
import type { Hop, WorldEvent } from './events'
import { HOLD_EXPIRY_DAYS, dueForSettlement, issuerAvailableMinor } from './select'
import { computeSettlement } from './settlement'
import type { CardState, Minor, Transaction, World } from './types'
import { initialWorld } from './world'

export type Action =
  | { type: 'reset' }
  | { type: 'advance-day' }
  | { type: 'authorise'; request: AuthRequest }
  | { type: 'clear'; transactionId: string; amountMinor: Minor }
  | { type: 'settle' }
  | { type: 'deliver-advice' }
  | { type: 'set-card-state'; cardId: string; state: CardState; reason: string }
  | { type: 'set-issuer-reachable'; reachable: boolean }
  | { type: 'set-stip-enabled'; enabled: boolean }
  | { type: 'set-vrm-rule'; ruleId: string; enabled: boolean }

export interface Applied {
  world: World
  events: WorldEvent[]
  decision?: AuthDecision
  hops?: Hop[]
}

/**
 * The single writer. Every state change goes through here and leaves an event
 * behind, which is what makes replay, stepping and permalinks fall out for free.
 *
 * Pure: same world and same action always give the same result. No clocks, no
 * randomness, no storage, and the input world is never mutated.
 */
export function apply(world: World, action: Action): Applied {
  switch (action.type) {
    case 'reset':
      return { world: initialWorld(), events: [{ kind: 'world.reset', seq: 0, day: 0 }] }

    case 'advance-day':
      return advanceDay(world)

    case 'authorise':
      return authorise(world, action.request)

    case 'clear':
      return clear(world, action.transactionId, action.amountMinor)

    case 'settle':
      return settle(world)

    case 'deliver-advice':
      return deliverAdvice(world)

    case 'set-card-state': {
      const card = world.cards.find((c) => c.id === action.cardId)
      if (!card || card.state === action.state) return { world, events: [] }
      const seq = world.seq + 1
      return {
        world: {
          ...world,
          seq,
          cards: world.cards.map((c) => (c.id === card.id ? { ...c, state: action.state } : c)),
        },
        events: [
          {
            kind: 'card.state-changed',
            seq,
            day: world.day,
            cardId: card.id,
            from: card.state,
            to: action.state,
            reason: action.reason,
          },
        ],
      }
    }

    case 'set-issuer-reachable': {
      const seq = world.seq + 1
      return {
        world: { ...world, seq, config: { ...world.config, issuerReachable: action.reachable } },
        events: [
          {
            kind: 'config.changed',
            seq,
            day: world.day,
            label: 'Issuer host reachable',
            enabled: action.reachable,
          },
        ],
      }
    }

    case 'set-stip-enabled': {
      const seq = world.seq + 1
      return {
        world: {
          ...world,
          seq,
          config: { ...world.config, stip: { ...world.config.stip, enabled: action.enabled } },
        },
        events: [
          {
            kind: 'config.changed',
            seq,
            day: world.day,
            label: 'Stand-In Processing',
            enabled: action.enabled,
          },
        ],
      }
    }

    case 'set-vrm-rule': {
      const rule = world.config.vrmRules.find((r) => r.id === action.ruleId)
      if (!rule) return { world, events: [] }
      const seq = world.seq + 1
      return {
        world: {
          ...world,
          seq,
          config: {
            ...world.config,
            vrmRules: world.config.vrmRules.map((r) =>
              r.id === rule.id ? { ...r, enabled: action.enabled } : r,
            ),
          },
        },
        events: [
          {
            kind: 'config.changed',
            seq,
            day: world.day,
            label: rule.label,
            enabled: action.enabled,
          },
        ],
      }
    }
  }
}

function authorise(world: World, request: AuthRequest): Applied {
  const decision = evaluateAuth(world, request)
  const merchant = world.merchants.find((m) => m.id === request.merchantId)
  const events: WorldEvent[] = []
  let seq = world.seq

  const id = `txn_${seq + 1}`
  events.push({
    kind: 'auth.requested',
    seq: ++seq,
    day: world.day,
    transactionId: id,
    amountMinor: request.amountMinor,
    merchantName: merchant?.name ?? 'Unknown merchant',
  })
  events.push({
    kind: 'auth.decided',
    seq: ++seq,
    day: world.day,
    transactionId: id,
    outcome: decision.outcome,
    responseCode: decision.responseCode,
    ...(decision.category !== undefined ? { category: decision.category } : {}),
    decidedAt: decision.decidedAt,
    decidedBy: decision.decidedBy,
    amountApprovedMinor: decision.amountApprovedMinor,
  })

  const approved = decision.outcome !== 'declined'
  const transaction: Transaction = {
    id,
    accountId: world.account.id,
    cardId: request.cardId,
    credential: request.credential,
    merchant: merchant ?? world.merchants[0]!,
    channel: request.channel,
    day: world.day,
    amountRequestedMinor: request.amountMinor,
    amountApprovedMinor: decision.amountApprovedMinor,
    outcome: decision.outcome,
    responseCode: decision.responseCode,
    ...(decision.category !== undefined ? { category: decision.category } : {}),
    decidedAt: decision.decidedAt,
    decidedBy: decision.decidedBy,
    clearings: [],
    standIn: decision.standIn,
    advised: decision.advised,
    ...(approved
      ? {
          hold: {
            amountMinor: decision.amountApprovedMinor,
            status: 'open' as const,
            placedDay: world.day,
            expiresDay: world.day + HOLD_EXPIRY_DAYS,
          },
        }
      : {}),
  }

  if (approved) {
    events.push({
      kind: 'hold.placed',
      seq: ++seq,
      day: world.day,
      transactionId: id,
      amountMinor: decision.amountApprovedMinor,
      expiresDay: world.day + HOLD_EXPIRY_DAYS,
      advised: decision.advised,
    })
  }

  return {
    world: { ...world, seq, transactions: [...world.transactions, transaction] },
    events,
    decision,
  }
}

/**
 * The 0120 advices Visa sends after standing in. Until these land, the
 * issuer's balance is simply wrong, and it has been authorising against a
 * number that does not reflect what the cardholder has already spent.
 */
function deliverAdvice(world: World): Applied {
  const pending = world.transactions.filter((t) => !t.advised && t.outcome !== 'declined')
  if (pending.length === 0) return { world, events: [] }

  const events: WorldEvent[] = []
  let seq = world.seq
  let runningAvailable = issuerAvailableMinor(world)

  for (const t of pending) {
    runningAvailable -= t.amountApprovedMinor
    events.push({
      kind: 'advice.delivered',
      seq: ++seq,
      day: world.day,
      transactionId: t.id,
      amountMinor: t.amountApprovedMinor,
      overdrawn: runningAvailable < 0,
    })
  }

  return {
    world: {
      ...world,
      seq,
      transactions: world.transactions.map((t) =>
        pending.some((p) => p.id === t.id) ? { ...t, advised: true } : t,
      ),
    },
    events,
  }
}

/** Clearing is a separate event from authorisation and can differ in amount. */
function clear(world: World, transactionId: string, amountMinor: Minor): Applied {
  const txn = world.transactions.find((t) => t.id === transactionId)
  if (!txn || txn.outcome === 'declined') return { world, events: [] }

  const events: WorldEvent[] = []
  let seq = world.seq
  const forcePost = txn.hold?.status !== 'open'

  const clearing = { id: `clr_${seq + 1}`, day: world.day, amountMinor, forcePost, settled: false }

  events.push({
    kind: 'clearing.posted',
    seq: ++seq,
    day: world.day,
    transactionId: txn.id,
    amountMinor,
    authAmountMinor: txn.amountApprovedMinor,
    forcePost,
  })
  if (txn.hold?.status === 'open') {
    events.push({
      kind: 'hold.released',
      seq: ++seq,
      day: world.day,
      transactionId: txn.id,
      amountMinor: txn.hold.amountMinor,
      reason: 'cleared',
    })
  }

  return {
    world: {
      ...world,
      seq,
      account: { ...world.account, ledgerMinor: world.account.ledgerMinor - amountMinor },
      transactions: world.transactions.map((t) =>
        t.id !== txn.id
          ? t
          : {
              ...t,
              clearings: [...t.clearings, clearing],
              ...(t.hold ? { hold: { ...t.hold, status: 'cleared' as const } } : {}),
            },
      ),
    },
    events,
  }
}

/**
 * Settlement proper. Money moves issuer to scheme to acquirer to merchant, and
 * each link keeps something. Nothing about this is visible to the cardholder.
 */
function settle(world: World): Applied {
  const due = dueForSettlement(world)
  if (due.length === 0) return { world, events: [] }

  const events: WorldEvent[] = []
  let seq = world.seq
  const settlements = [...world.settlements]

  for (const row of due) {
    const s = computeSettlement(
      `stl_${seq + 1}`,
      row.transaction.id,
      row.clearingId,
      world.day,
      row.amountMinor,
      world.config.fees,
    )
    settlements.push(s)
    events.push({
      kind: 'settlement.executed',
      seq: ++seq,
      day: world.day,
      transactionId: row.transaction.id,
      grossMinor: s.grossMinor,
      interchangeMinor: s.interchangeMinor,
      schemeFeeMinor: s.schemeFeeMinor,
      merchantNetMinor: s.merchantNetMinor,
    })
  }

  const settledIds = new Set(due.map((d) => d.clearingId))
  return {
    world: {
      ...world,
      seq,
      settlements,
      transactions: world.transactions.map((t) => ({
        ...t,
        clearings: t.clearings.map((c) => (settledIds.has(c.id) ? { ...c, settled: true } : c)),
      })),
    },
    events,
  }
}

/** Moving the clock is what separates authorisation, clearing and settlement. */
function advanceDay(world: World): Applied {
  const day = world.day + 1
  const events: WorldEvent[] = []
  let seq = world.seq

  events.push({ kind: 'day.advanced', seq: ++seq, day, from: world.day })

  const transactions = world.transactions.map((t) => {
    if (t.hold?.status === 'open' && day > t.hold.expiresDay) {
      events.push({
        kind: 'hold.released',
        seq: ++seq,
        day,
        transactionId: t.id,
        amountMinor: t.hold.amountMinor,
        reason: 'expired',
      })
      return { ...t, hold: { ...t.hold, status: 'expired' as const } }
    }
    return t
  })

  return { world: { ...world, day, seq, transactions }, events }
}
